import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { UploadSimple, LinkSimple, Sparkle, Check } from "@phosphor-icons/react";

export default function SubmitLink() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const tenantSlug = sp.get("tenant") || null;
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ title: "", url: "", price: "", description: "", category_id: "", image_url: "" });
  const [imageMode, setImageMode] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [purchaseOn, setPurchaseOn] = useState(false);
  useEffect(() => { api.get("/store-settings").then(({ data }) => setPurchaseOn(!!data.links)).catch(() => {}); }, []);

  useEffect(() => {
    const p = tenantSlug ? { tenant: tenantSlug } : {};
    api.get("/categories", { params: p }).then((r) => setCategories(r.data));
    api.get("/pricing-tiers", { params: p }).then((r) => { setTiers(r.data); if (r.data[0]) setSelectedTier(r.data[0].id); });
  }, [tenantSlug]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image_url", data.url);
    } catch (e) { setErr("Image upload failed."); }
    finally { setUploading(false); }
  };

  const pay = async () => {
    setErr("");
    if (!form.title || !form.url || !form.category_id) { setErr("Please fill in title, URL and category."); return; }
    if (!selectedTier) { setErr("Please choose a plan."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier_id: selectedTier,
        origin_url: window.location.origin,
        tenant_slug: tenantSlug,
        link: { ...form, is_featured: false },
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Post Your <span className="text-[#FF007F]">Link</span></h1>
      <p className="text-zinc-400 mt-2 mb-8">Your link gets a glowing <span className="text-[#FF007F] font-bold">SPONSORED</span> badge and rises to the top so everyone spots it.</p>

      {err && <p className="text-[#FF007F] text-sm mb-4 border border-[#FF007F] p-3" data-testid="submit-error">{err}</p>}

      <div className="grid gap-4 mb-8">
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Title *</label>
          <input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid={TID.submitTitle}
            className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Affiliate URL *</label>
            <input value={form.url} onChange={(e) => upd("url", e.target.value)} placeholder="https://…" data-testid={TID.submitUrl}
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Price / Offer</label>
            <input value={form.price} onChange={(e) => upd("price", e.target.value)} placeholder="$49.99 / 20% OFF" data-testid={TID.submitPrice}
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Category *</label>
          <select value={form.category_id} onChange={(e) => upd("category_id", e.target.value)} data-testid={TID.submitCategory}
            className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white">
            <option value="">Select category…</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Description</label>
          <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3} data-testid={TID.submitDescription}
            className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
        </div>

        {/* Image */}
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Image</label>
          <div className="flex gap-2 mt-1 mb-3">
            <button type="button" onClick={() => setImageMode("upload")} className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border ${imageMode === "upload" ? "border-[#00FFFF] text-[#00FFFF]" : "border-zinc-700 text-zinc-400"}`}><UploadSimple size={14} /> Upload</button>
            <button type="button" onClick={() => setImageMode("url")} className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border ${imageMode === "url" ? "border-[#00FFFF] text-[#00FFFF]" : "border-zinc-700 text-zinc-400"}`}><LinkSimple size={14} /> Paste URL</button>
          </div>
          {imageMode === "upload" ? (
            <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0])} data-testid={TID.submitImageFile}
              className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#FF007F] file:text-white file:font-bold file:uppercase file:text-xs" />
          ) : (
            <input value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} placeholder="https://image-url…" data-testid={TID.submitImageUrl}
              className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
          )}
          {uploading && <p className="text-xs text-[#00FFFF] mt-2">Uploading…</p>}
          {form.image_url && !uploading && <p className="text-xs text-[#39FF14] mt-2 flex items-center gap-1"><Check size={14} /> Image ready</p>}
        </div>
      </div>

      {/* Pricing tiers */}
      <h2 className="font-display text-2xl font-black uppercase mb-4">Choose a <span className="text-[#00FFFF]">Plan</span></h2>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {tiers.map((t, i) => (
          <button key={t.id} type="button" onClick={() => setSelectedTier(t.id)} data-testid={TID.tierCard}
            className={`relative text-left p-5 border transition-all ${selectedTier === t.id ? "border-[#FF007F] shadow-[6px_6px_0px_#FF007F]" : "border-zinc-700 hover:border-zinc-500"}`}>
            {i === tiers.length - 1 && <span className="absolute top-2 right-2 bg-[#FFD600] text-black text-[10px] font-black uppercase px-2 py-0.5 font-mono">Best Deal</span>}
            <p className="text-xs uppercase tracking-widest text-zinc-400">{t.name}</p>
            <p className="font-mono font-extrabold text-3xl text-[#39FF14] mt-1">${t.price}</p>
            <p className="text-sm text-zinc-400 mt-1">{t.days} days live</p>
            {selectedTier === t.id && <Sparkle size={18} weight="fill" color="#FF007F" className="mt-3" />}
          </button>
        ))}
      </div>

      {purchaseOn ? (
      <button onClick={pay} disabled={busy} data-testid={TID.submitPayBtn}
        className="w-full sm:w-auto px-8 py-4 bg-[#FF007F] text-white font-black uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors disabled:opacity-50 hard-shadow">
        {busy ? "Redirecting to checkout…" : "Pay & Publish"}
      </button>
      ) : (
      <button disabled data-testid={TID.submitPayBtn}
        className="w-full sm:w-auto px-8 py-4 bg-zinc-700 text-zinc-300 font-black uppercase tracking-widest border border-zinc-600 cursor-not-allowed hard-shadow">
        Coming Soon
      </button>
      )}
      <p className="text-xs text-zinc-500 mt-3">{purchaseOn ? "Secure checkout via Stripe. Cash App Pay & cards accepted." : "🚀 Paid link submissions open soon! We're finalizing checkout — check back shortly to feature your deal."}</p>
    </div>
  );
}
